import { Dropzone, OrderTrackingFileList } from "@components";
import { Edit } from "@mui/icons-material";
import {
  Button,
  Stack,
  Skeleton,
  Snackbar,
  Divider,
  Typography,
  CircularProgress,
  Card,
  TextField,
} from "@mui/material";
import { debounce } from "lodash";
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  OrderOut,
  useWebApiV1UpdateOrderMutation,
} from "@providers/hop-ord-server/api";
import SendForReviewDialog from "../SendForReviewDialog/SendForReviewDialog";
import SendIcon from "@mui/icons-material/Send";
import { ExitPrompt } from "./ExitPrompt";
import { OrderStatus } from "@enums";
import useManageAttachments, {
  CompletedMedia,
  PendingScorecard,
} from "@utils/useManageAttachments";

const PAGE_WIDTH = "900px"; // Need to enforce the width so the PDF generation is consistent

interface Props {
  orderId: number;
  orderData?: OrderOut;
}

const UploadPanel = ({ orderId, orderData }: Props) => {
  const {
    attachmentMap,
    deleteAttachment,
    handleDrop,
    busy,
    commitAttachments,
  } = useManageAttachments(orderId);

  const [trackingMode, setTrackingMode] = useState<
    "edit" | "submitted" | "unknown"
  >("unknown");

  useEffect(() => {
    // if any completed files have been sent for review and the mode is unknown,
    // set the mode to submitted
    setTrackingMode((prev: "edit" | "submitted" | "unknown") => {
      if (prev === "unknown") {
        const anySentForReview = Array.from(attachmentMap.values()).some(
          (att) => att.state === "completed" && att.sentForReview,
        );
        if (anySentForReview) {
          return "submitted";
        } else if (!busy) {
          return "edit";
        }
      }
      return prev;
    });
  }, [attachmentMap, busy, trackingMode]);

  // TODO: can't iterate over a map directly until we enable es2025
  const attachmentEntries = useMemo(
    () => Array.from(attachmentMap.entries()),
    [attachmentMap],
  );

  const hasFilesToSend = useMemo(() => {
    return attachmentEntries.some(
      ([_, att]) =>
        (att.state === "completed" && !att.sentForReview) ||
        (att.category === "scorecard" && att.state === "pending"),
    );
  }, [attachmentEntries]);

  const [updateOrder] = useWebApiV1UpdateOrderMutation();
  const [summary, setSummary] = useState("");

  // Initialize summary from order data
  useEffect(() => {
    if (orderData?.planReviewSummary) {
      setSummary(orderData.planReviewSummary);
    }
  }, [orderData]);

  // Debounced function to update the order
  const debouncedUpdateOrder = useCallback(
    debounce(async (summaryText: string) => {
      if (orderId) {
        try {
          await updateOrder({
            orderId: Number(orderId),
            orderIn: {
              planReviewSummary: summaryText,
            },
          }).unwrap();
        } catch (error) {
          console.error("Failed to update order summary:", error);
        }
      }
    }, 1000),
    [orderId, updateOrder],
  );

  // Effect to trigger debounced update when summary changes
  useEffect(() => {
    if (summary !== (orderData?.planReviewSummary || "")) {
      debouncedUpdateOrder(summary);
    }
  }, [summary, debouncedUpdateOrder, orderData?.planReviewSummary]);

  const [orderStatusUpdate] = useWebApiV1UpdateOrderMutation();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [openToast, setOpenToast] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSendForReview = useCallback(async () => {
    // Immediately close the dialog
    setDialogOpen(false);
    setIsSubmitting(true);

    try {
      await commitAttachments(attachmentMap);
      await orderStatusUpdate({
        orderId: Number(orderId),
        orderIn: { status: OrderStatus.READY_FOR_RO_APPROVAL },
      });

      // Show success message after completion
      setOpenToast(true);
    } catch (error) {
      console.error("Error submitting for review:", error);
      // Handle error (you might want to show an error message to the user)
    } finally {
      setIsSubmitting(false);
      setTrackingMode("submitted");
    }
  }, [attachmentMap, orderId, orderStatusUpdate, commitAttachments]);

  if (!orderData) {
    return (
      <Stack flex={1} gap={1} data-testid="loading-scorecard-upload">
        <Skeleton variant="text" width={200} height={45} />
        <Skeleton variant="rounded" height={145} />
        <Skeleton variant="text" width={250} height={32} />
        <Skeleton variant="rounded" height={100} />
        <Stack direction="row-reverse">
          <Skeleton width={200} height={32} variant="rounded" />
        </Stack>
      </Stack>
    );
  }

  const areFilesUploading = attachmentEntries.some(
    ([_, att]) => att.state === "uploading",
  );

  const showEditFilesButton =
    trackingMode !== "edit" &&
    ![OrderStatus.COMPLETED, OrderStatus.APPROVED].includes(orderData.status);

  // Enable the send for review button if we are not submitting, if there are
  // files to send and we are in edit mode, or if we are in edit mode and the
  // summary is different from the order data.
  const isSendForReviewButtonEnabled =
    !isSubmitting &&
    trackingMode === "edit" &&
    (hasFilesToSend || summary !== (orderData.planReviewSummary ?? "")) &&
    !areFilesUploading;

  return (
    <Stack gap={2}>
      <Stack gap={0.5}>
        <TextField
          variant="outlined"
          fullWidth
          multiline={true}
          minRows={2}
          maxRows={3}
          value={summary}
          placeholder="Briefly explain the plan, any limitations, or key decisions..."
          label="Summary"
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setSummary(event.currentTarget.value);
          }}
          data-testid="plan-review-summary"
          disabled={
            orderData?.status === OrderStatus.READY_FOR_RO_APPROVAL &&
            trackingMode !== "edit"
          }
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          helperText="Optional notes to help the physician review this plan more efficiently."
        />
      </Stack>
      <Card
        sx={{
          maxWidth: PAGE_WIDTH,
          width: 1,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <Stack overflow="auto" height={1} width={1}>
          {trackingMode === "unknown" || isSubmitting ? (
            <Stack
              sx={{
                padding: 2,
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CircularProgress />
            </Stack>
          ) : (
            <Stack padding={2}>
              {trackingMode !== "submitted" && (
                <Dropzone handleDropFiles={handleDrop ?? (() => {})} />
              )}
              {trackingMode === "submitted" && (
                <Typography variant="subtitle1">
                  Submitted for Approval
                </Typography>
              )}
              {attachmentMap.size > 0 && (
                <OrderTrackingFileList
                  attachmentEntries={attachmentEntries}
                  isEditMode={trackingMode === "edit"}
                  disabled={isSubmitting}
                  deleteAttachment={deleteAttachment}
                />
              )}
            </Stack>
          )}
          <Divider />
          <Stack
            direction="row-reverse"
            spacing={2}
            padding={1}
            justifyContent="space-between"
          >
            <Button
              variant="contained"
              onClick={() => setDialogOpen(true)}
              disabled={!isSendForReviewButtonEnabled}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SendIcon />
                )
              }
            >
              {isSubmitting ? "Sending..." : "Send for review"}
            </Button>
            {showEditFilesButton && (
              <Button
                variant="outlined"
                startIcon={<Edit />}
                onClick={() => setTrackingMode("edit")}
              >
                Edit Files
              </Button>
            )}
          </Stack>
          <SendForReviewDialog
            open={dialogOpen}
            setOpen={setDialogOpen}
            onConfirm={handleSendForReview}
            attachments={attachmentEntries
              .map(([_, att]) => att)
              .filter(
                (att): att is CompletedMedia | PendingScorecard =>
                  (att.state === "completed" && !att.toDelete) ||
                  (att.category === "scorecard" && att.state === "pending"),
              )}
            orderData={orderData}
          />
          <ExitPrompt
            when={hasFilesToSend}
            message="Unsubmitted files will be lost if you leave this page."
          />
          <Snackbar
            open={openToast}
            autoHideDuration={6000}
            onClose={() => setOpenToast(false)}
            message="Success! Files have been sent to the physician for review"
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          />
        </Stack>
      </Card>
    </Stack>
  );
};

export default UploadPanel;
